import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Modal from '../../components/common/Modal';
import { TicketTypeIcon, TICKET_TYPES } from '../../components/common/TicketTypeSelector';
import api from '../../services/api';
import { validationMessages } from '../../utils/validationMessages';
import './TaskTypeManager.css';

/**
 * TaskTypeManager - Admin-only page for managing global task types
 * Allows creating, updating, deleting, and uploading icons for task types
 */
const TaskTypeManager = () => {
  const { user } = useSelector((state) => state.auth);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showIconUploadModal, setShowIconUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    type: null,
    loadingCount: false,
    taskCount: 0,
    replacementTypeId: '',
    isSubmitting: false
  });

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    is_subtask_type: false
  });

  // Check if user is admin
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  // Fetch all task types
  const fetchTaskTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/task-types?include_inactive=true');
      setTaskTypes(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch task types:', error);
      toast.error('Failed to load task types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchTaskTypes();
    }
  }, [isAdmin, fetchTaskTypes]);

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle create task type
  const handleCreateTaskType = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(validationMessages.taskType.name.required);
      return;
    }
    if (!formData.key.trim()) {
      toast.error(validationMessages.taskType.key.required);
      return;
    }

    try {
      await api.post('/task-types', formData);
      toast.success('Task type created successfully');
      setShowCreateModal(false);
      setFormData({
        name: '',
        key: '',
        description: '',
        is_subtask_type: false
      });
      await fetchTaskTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task type');
    }
  };

  // Handle update task type
  const handleUpdateTaskType = async (e) => {
    e.preventDefault();
    if (!selectedType) return;

    try {
      await api.patch(`/task-types/${selectedType.id}`, formData);
      toast.success('Task type updated successfully');
      setShowEditModal(false);
      setSelectedType(null);
      await fetchTaskTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task type');
    }
  };

  // Handle icon upload
  const handleIconUpload = async () => {
    if (!selectedFile || !selectedType) {
      toast.error(validationMessages.taskType.icon.required);
      return;
    }

    setUploading(true);
    try {
      const formDataFile = new FormData();
      formDataFile.append('file', selectedFile);

      await api.post(`/task-types/${selectedType.id}/icon/upload`, formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Icon uploaded successfully');
      setShowIconUploadModal(false);
      setSelectedFile(null);
      await fetchTaskTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload icon');
    } finally {
      setUploading(false);
    }
  };

  // Handle delete task type — opens migration modal and fetches usage count
  const handleDeleteTaskType = async (type) => {
    if (type.is_system_default) {
      toast.error(validationMessages.taskType.system.cannotDelete);
      return;
    }

    setDeleteModal({
      isOpen: true,
      type,
      loadingCount: true,
      taskCount: 0,
      replacementTypeId: '',
      isSubmitting: false
    });

    try {
      const response = await api.get(`/task-types/${type.id}/usage`);
      setDeleteModal(prev => ({
        ...prev,
        loadingCount: false,
        taskCount: response.data.task_count
      }));
    } catch (error) {
      toast.error('Failed to check task usage');
      setDeleteModal(prev => ({ ...prev, isOpen: false }));
    }
  };

  const handleConfirmDelete = async () => {
    const { type, taskCount, replacementTypeId } = deleteModal;
    if (!type) return;

    if (taskCount > 0 && !replacementTypeId) {
      toast.error('Please select a replacement type for existing tasks');
      return;
    }

    setDeleteModal(prev => ({ ...prev, isSubmitting: true }));

    try {
      const params = taskCount > 0 ? { replacement_type_id: replacementTypeId } : {};
      await api.delete(`/task-types/${type.id}`, { params });
      toast.success('Task type deleted successfully');
      setDeleteModal({ isOpen: false, type: null, loadingCount: false, taskCount: 0, replacementTypeId: '', isSubmitting: false });
      setSelectedType(null);
      await fetchTaskTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task type');
      setDeleteModal(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  // Open edit modal
  const handleEditType = (type) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      key: type.key,
      description: type.description || '',
      is_subtask_type: type.is_subtask_type
    });
    setShowEditModal(true);
  };

  if (!isAdmin) {
    return (
      <div className="task-type-manager access-denied">
        <p>You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="task-type-manager loading">Loading task types...</div>;
  }

  const systemTypes = taskTypes.filter(t => t.is_system_default);
  const customTypes = taskTypes.filter(t => !t.is_system_default);

  return (
    <div className="task-type-manager">
      <div className="page-header">
        <div>
          <h1>Task Type Manager</h1>
          <p className="page-description">Manage task types and their icons</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            setFormData({
              name: '',
              key: '',
              description: '',
              is_subtask_type: false
            });
            setShowCreateModal(true);
          }}
        >
          + Create Task Type
        </button>
      </div>

      <div className="manager-container">
        {/* System Types */}
        <section className="types-section">
          <h2>System Default Types</h2>
          <div className="types-list">
            {systemTypes.map(type => {
              const fallbackConfig = TICKET_TYPES[type.key] || {};
              return (
                <div key={type.id} className="type-card system-type">
                  <div className="type-row">
                    <div className="type-info">
                      <TicketTypeIcon
                        type={type.key}
                        size="sm"
                        config={{
                          icon: type.icon_url || fallbackConfig.icon || '?',
                          color: fallbackConfig.color || '#4c9aff',
                          bgColor: fallbackConfig.bgColor || '#deebff',
                          label: type.name
                        }}
                      />
                      <div className="type-details">
                        <div className="type-name">{type.name}</div>
                        <div className="type-meta">
                          <code>{type.key}</code>
                          <span className="badge">System Default</span>
                          {type.is_subtask_type && <span className="badge subtask">Subtask</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="type-actions">
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => {
                        setSelectedType(type);
                        setShowIconUploadModal(true);
                      }}
                    >
                      Upload Icon
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Custom Types */}
        {customTypes.length > 0 && (
          <section className="types-section">
            <h2>Custom Types</h2>
            <div className="types-list">
              {customTypes.map(type => {
                const baseConfig = type.is_subtask_type ? TICKET_TYPES.subtask : TICKET_TYPES.task;
                return (
                  <div key={type.id} className="type-card custom-type">
                    <div className="type-row">
                      <div className="type-info">
                        <TicketTypeIcon
                          type={type.id}
                          size="sm"
                          config={{
                            icon: type.icon_url || type.name?.charAt(0).toUpperCase() || baseConfig.icon,
                            color: baseConfig.color,
                            bgColor: baseConfig.bgColor,
                            label: type.name
                          }}
                        />
                        <div className="type-details">
                          <div className="type-name">{type.name}</div>
                          <div className="type-meta">
                            <code>{type.key}</code>
                            {type.is_subtask_type && <span className="badge subtask">Subtask</span>}
                            {!type.is_active && <span className="badge inactive">Inactive</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="type-actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEditType(type)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => {
                          setSelectedType(type);
                          setShowIconUploadModal(true);
                        }}
                      >
                        Upload Icon
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTaskType(type)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {customTypes.length === 0 && (
          <section className="types-section empty">
            <p>No custom task types yet. Create one to get started!</p>
          </section>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Task Type"
        size="small"
      >
        <form onSubmit={handleCreateTaskType}>
          <div className="form-group">
            <label className="form-label">Type Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              placeholder="e.g., Feature Request"
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Type Key <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              name="key"
              value={formData.key}
              onChange={handleFormChange}
              placeholder="e.g., feature_request"
            />
            <small className="form-help">Unique identifier for the type</small>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              placeholder="Optional description..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="is_subtask_type"
                checked={formData.is_subtask_type}
                onChange={handleFormChange}
              />
              This is a subtask type
            </label>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" type="button" onClick={() => setShowCreateModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit">
              Create
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Edit ${selectedType?.name}`}
        size="small"
      >
        <form onSubmit={handleUpdateTaskType}>
          <div className="form-group">
            <label className="form-label">Type Name <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              placeholder="e.g., Feature Request"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Type Key <span className="required">*</span></label>
            <input
              type="text"
              className="form-input"
              name="key"
              value={formData.key}
              onChange={handleFormChange}
              placeholder="e.g., feature_request"
              disabled={selectedType?.is_system_default}
            />
            <small className="form-help">
              {selectedType?.is_system_default ? 'Cannot change key for system types' : 'Unique identifier for the type'}
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              name="description"
              value={formData.description}
              onChange={handleFormChange}
              placeholder="Optional description..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="is_subtask_type"
                checked={formData.is_subtask_type}
                onChange={handleFormChange}
                disabled={selectedType?.is_system_default}
              />
              This is a subtask type
            </label>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" type="button" onClick={() => setShowEditModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit">
              Save Changes
            </button>
          </div>
        </form>
      </Modal>

      {/* Icon Upload Modal */}
      <Modal
        isOpen={showIconUploadModal}
        onClose={() => {
          setShowIconUploadModal(false);
          setSelectedFile(null);
        }}
        title={`Upload Icon for ${selectedType?.name}`}
        size="small"
      >
        <div className="form-group">
          <label className="form-label">Icon File <span className="required">*</span></label>
          <div className="file-input-wrapper">
            <input
              type="file"
              accept="image/png,image/svg+xml,image/webp"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              className="form-input-file"
            />
            <p className="form-help">
              Accepted formats: PNG, SVG, WEBP (max 1MB)
            </p>
          </div>
          {selectedFile && (
            <div className="file-preview">
              <p>Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" type="button" onClick={() => {
            setShowIconUploadModal(false);
            setSelectedFile(null);
          }}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleIconUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </Modal>

      {/* Delete / Migration Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => !deleteModal.isSubmitting && setDeleteModal(prev => ({ ...prev, isOpen: false }))}
        title={`Delete "${deleteModal.type?.name}"`}
        size="small"
      >
        {deleteModal.loadingCount ? (
          <div className="delete-modal-loading">
            <div className="spinner-sm" />
            <span>Checking task usage...</span>
          </div>
        ) : (
          <>
            {deleteModal.taskCount > 0 ? (
              <>
                <div className="delete-modal-warning">
                  <strong>{deleteModal.taskCount} task{deleteModal.taskCount !== 1 ? 's' : ''}</strong> across all projects are using this type.
                  Select a replacement type to migrate them before deleting.
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Migrate tasks to <span className="required">*</span>
                  </label>
                  <select
                    className="form-input"
                    value={deleteModal.replacementTypeId}
                    onChange={e => setDeleteModal(prev => ({ ...prev, replacementTypeId: e.target.value }))}
                  >
                    <option value="">— Select replacement type —</option>
                    {taskTypes
                      .filter(t => t.id !== deleteModal.type?.id && t.is_active && t.is_subtask_type === deleteModal.type?.is_subtask_type)
                      .map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))
                    }
                  </select>
                </div>
              </>
            ) : (
              <p className="delete-modal-confirm-text">
                Are you sure you want to delete <strong>{deleteModal.type?.name}</strong>? This action cannot be undone.
              </p>
            )}

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
                disabled={deleteModal.isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                type="button"
                onClick={handleConfirmDelete}
                disabled={deleteModal.isSubmitting || (deleteModal.taskCount > 0 && !deleteModal.replacementTypeId)}
              >
                {deleteModal.isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default TaskTypeManager;
